import React from "react";

import { css } from "@emotion/css";
import { useTranslation } from "react-i18next";

import { useStyles } from "@/hooks/useStyles.ts";
import { alertCategoriesTranslations, AlertConfig, MeasurementAlertParameters } from "@/models/AlertModel.ts";
import { HappenedInTheLastXDays } from "@/pages/patient-settings/alert-settings/inputs/HappenedInTheLastXDays.tsx";
import { Queries } from "@/queries/Queries.ts";
import { Theme } from "@/theme/CreateTheme.ts";
import { TextToast } from "@/uiKit/atoms/Toast.tsx";
import { formatNumber } from "@/utils/mathUtils.ts";

import { NumberOfOccurrences } from "../inputs/NumberOfOccurrences";

export type AlertMeasurementSettingsProps = {
  patientId: string;
  alertConfig: AlertConfig;
  parameters: MeasurementAlertParameters;
};

export const AlertMeasurementSettings: React.FC<AlertMeasurementSettingsProps> = ({
  patientId,
  alertConfig,
  parameters,
}) => {
  const styles = useStyles(makeStyles);
  const { t } = useTranslation();
  const editAlertConfig = Queries.alerts.useEditAlertConfig();
  const updateParameters = (value: unknown, key: keyof MeasurementAlertParameters) => {
    editAlertConfig.mutate(
      {
        patientId,
        alertConfig: {
          id: alertConfig.id,
          parameters: { ...parameters, [key as string]: value },
        },
      },
      {
        onSuccess: () =>
          TextToast.success(
            t("alertConfig.conditionsSuccess", {
              alert: t(alertCategoriesTranslations.observance_measures),
            }),
          ),
        onError: () =>
          TextToast.error(
            t("alertConfig.conditionsError", {
              alert: t(alertCategoriesTranslations.observance_measures),
            }),
          ),
      },
    );
  };

  return (
    <div className={styles.container}>
      <NumberOfOccurrences
        id={`alert-config-${alertConfig.id}-occurrences`}
        textBefore={t("alertConfig.atLeast")}
        textAfter={t("alertConfig.measuresPerDayNoMinumum")}
        onCountChange={count => updateParameters(count, "minNumberOfMeasuresPerDay")}
        minCount={1}
        maxCount={999}
        count={formatNumber(parameters.minNumberOfMeasuresPerDay, 0)}
      />
      <HappenedInTheLastXDays
        id={`alert-config-${alertConfig.id}-days`}
        onDaysChange={days => updateParameters(days, "numberOfDays")}
        minDays={1}
        maxDays={7}
        days={parameters.numberOfDays}
      />
    </div>
  );
};

const makeStyles = (theme: Theme) => ({
  container: css`
    display: flex;
    flex-direction: column;
    gap: ${theme.spacing(2)};
  `,
  row: css`
    display: flex;
    flex-direction: row;
  `,
});
